/**
 * Someone fallen: a ragdoll tumbling to rest, a moment lying there, then
 * getting up. Shared by the player thrown off or knocked down, the villagers
 * driven into, and other players as this game sees them fall.
 *
 * The body is a ragdoll (`core/character/ragdoll.ts`) started from the pose
 * last drawn for that person, so nothing jumps. Once it has lain still a
 * moment it gets up: into a crouch over the hips, then standing, blended
 * point by point and made a body again each frame. The crouch and the stand
 * are the crowd's own poses, read back as points. Scene coordinates
 * throughout: whoever owns one moves it with the scene's origin (`shift`).
 */

import type { GaitPose } from '../../core/character/gait.js';
import { gaitPose } from '../../core/character/gait.js';
import type { Ragdoll, RagdollCollide, RagdollGround } from '../../core/character/ragdoll.js';
import { J, blendToward, faceDown, hips, ragdollFrom, shiftRagdoll, stepRagdoll } from '../../core/character/ragdoll.js';
import type { CharacterCrowd } from './crowd.js';

/** Seconds lying still before getting up; then into a crouch, and up from it. */
export const LIE_STILL = 1.2;
const RISE_CROUCH = 0.8;
const RISE_STAND = 0.7;

/** Squatting on the heels, leaning over the knees, hands forward: halfway up from the ground. */
const CROUCH: GaitPose = {
  bob: -0.38, lean: 0.7, sway: 0, twist: 0, spread: 0.12, nod: -0.35,
  thigh: { left: 2.0, right: 2.0 }, knee: { left: 1.8, right: 1.8 }, ankle: { left: 0.5, right: 0.5 },
  shoulder: { left: 0.9, right: 0.9 }, elbow: { left: 0.5, right: 0.5 },
};

/** The world as a body meets it: the ground under it and what it falls against. */
export interface BodyWorld {
  ground: RagdollGround;
  collide?: RagdollCollide;
}

/** Where someone got up: on their feet at (x, y, z), facing `yaw`. */
export interface Stood {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

interface Rise {
  t: number;
  from: Float32Array;
  crouch: Float32Array;
  stand: Float32Array;
  at: Stood;
}

const ease = (k: number) => k * k * (3 - 2 * k);

export class Fallen {
  readonly body: Ragdoll;
  /** Seconds lying still. */
  private lying = 0;
  private rise: Rise | undefined;
  /** Kept down until let up: another player's own game says when they stand. */
  hold = false;
  /** Up again, and where. */
  stood: Stood | undefined;
  /** What stepping it has cost, ms, over how many frames, and the worst: for measuring. */
  ms = 0;
  frames = 0;
  worst = 0;

  /** Someone fallen from the pose crowd slot `slot` was last drawn in, still: set them moving on `body`. */
  constructor(private readonly crowd: CharacterCrowd, private readonly slot: number, readonly helmet = false) {
    this.body = ragdollFrom(crowd.jointsOf(slot));
  }

  get rising(): boolean {
    return this.rise !== undefined;
  }

  /** Between the hips: where the body is. */
  get at(): [number, number, number] {
    return hips(this.body.p);
  }

  /** The scene's origin moved by (dx, dz) under it. */
  shift(dx: number, dz: number): void {
    shiftRagdoll(this.body, dx, dz);
    const r = this.rise;
    if (r === undefined) return;
    for (const b of [r.from, r.crouch, r.stand]) for (let i = 0; i < b.length; i += 3) { b[i] += dx; b[i + 2] += dz; }
    r.at.x += dx;
    r.at.z += dz;
  }

  /**
   * On by `dt` seconds: tumbling, lying, or getting up. `clock` sways the
   * standing pose as it breathes; `standAt`, if given, is where to get up
   * rather than where the body lies (another player's real place).
   */
  step(dt: number, world: BodyWorld, clock: number, standAt?: { x: number; z: number }): void {
    if (this.stood !== undefined) return;
    const body = this.body;
    if (this.rise === undefined) {
      if (!body.resting) {
        const t0 = performance.now();
        stepRagdoll(body, dt, world.ground, world.collide);
        const ms = performance.now() - t0;
        this.ms += ms;
        this.frames++;
        this.worst = Math.max(this.worst, ms);
      } else {
        this.lying += dt;
      }
      if (this.lying >= LIE_STILL && !this.hold) this.rise = this.risingFrom(world, clock, standAt);
      return;
    }
    const r = this.rise;
    r.t += dt;
    if (r.t < RISE_CROUCH) blendToward(body, r.from, r.crouch, ease(r.t / RISE_CROUCH), world.ground);
    else blendToward(body, r.crouch, r.stand, ease(Math.min(1, (r.t - RISE_CROUCH) / RISE_STAND)), world.ground);
    if (r.t >= RISE_CROUCH + RISE_STAND) this.stood = r.at;
  }

  /** Draw them as they lie, tumble or get up. */
  draw(): void {
    this.crowd.placeRagdoll(this.slot, this.body.p, this.helmet);
  }

  /**
   * Getting up: over the hips, facing the way the head lies if face down
   * (pushed up onto the knees), the way the feet lie if on the back (sat up
   * first).
   */
  private risingFrom(world: BodyWorld, clock: number, standAt?: { x: number; z: number }): Rise {
    const p = this.body.p;
    const [hx, hy, hz] = hips(p);
    const way = faceDown(p) ? 1 : -1;
    const fx = (p[J.head * 3] - hx) * way, fz = (p[J.head * 3 + 2] - hz) * way;
    const yaw = Math.atan2(-fx, -fz);
    const x = standAt?.x ?? hx, z = standAt?.z ?? hz;
    const y = world.ground(x, z, hy) ?? hy - 0.1;
    this.crowd.place(this.slot, { x, y, z, yaw, pose: CROUCH, visible: true });
    const crouch = this.crowd.jointsOf(this.slot);
    this.crowd.place(this.slot, { x, y, z, yaw, pose: gaitPose(0, 0, clock), visible: true });
    const stand = this.crowd.jointsOf(this.slot);
    return { t: 0, from: Float32Array.from(p), crouch, stand, at: { x, y, z, yaw } };
  }
}
